import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

// Must match Mongoose maxlength on Studio.tcs and the cap in
// routes/studioRoutes.js PUT /tcs.
const MAX_LEN = 20000;

const PLACEHOLDER = `Add your studio's Terms & Conditions here. Students will see this and must check "I agree" before paying at enrollment.

Markdown is supported. A typical structure:

# Enrollment

Describe how students sign up, the commitment length, when payment is due.

# Payment & Refunds

Be clear about your refund policy — what's refundable, what isn't, and when.

# Liability & Waiver

Disclaimers about injury risk, that you are not a medical professional, and that students should consult their own doctor for serious conditions.

# Photo, Video & Social Media Consent

Whether classes are recorded, whether clips appear on social media, what minors' parents need to sign.

# Class Cancellations

How you handle class cancellations, what happens to missed classes.

# Code of Conduct

What you expect from students.

# Privacy

How student data is handled.

# Contact

How students reach you for questions.`;

const TCsTab = () => {
  const { studio, refreshUser } = useAuth();
  const [tcs, setTcs] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setTcs(studio?.tcs || '');
  }, [studio?.tcs]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await api.put('/studio/tcs', { tcs });
      setSaved(true);
      await refreshUser();
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save terms & conditions');
    } finally {
      setSaving(false);
    }
  };

  const length = tcs.length;
  const overLimit = length > MAX_LEN;

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Terms &amp; Conditions</h1>
      <p className="text-sm text-gray-500 mb-2">
        Studio-wide Terms &amp; Conditions. Surfaced as a checkbox on the enrollment / payment page —
        students must agree before paying. Recorded against the enrollment, so you have a clean audit
        trail of who agreed to what when.
      </p>
      <p className="text-xs text-gray-400 mb-6">
        Plain text is fine. Newlines and basic punctuation are preserved when shown to students.
      </p>

      <div className="border border-gray-200 rounded-lg bg-white">
        <textarea
          value={tcs}
          onChange={e => {
            setTcs(e.target.value);
            setSaved(false);
          }}
          placeholder={PLACEHOLDER}
          rows={24}
          className="w-full px-4 py-3 text-sm font-mono leading-relaxed border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-y"
          spellCheck="true"
        />
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <p className={`text-xs ${overLimit ? 'text-red-600 font-medium' : length > MAX_LEN - 1000 ? 'text-amber-600' : 'text-gray-400'}`}>
            {length.toLocaleString()} / {MAX_LEN.toLocaleString()} characters
            {overLimit && ` — trim ${(length - MAX_LEN).toLocaleString()}`}
          </p>
          <div className="flex items-center gap-3">
            {saved && <span className="text-xs text-green-600">Saved</span>}
            {error && <span className="text-xs text-red-600">{error}</span>}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || overLimit}
              className="px-4 py-2 text-sm font-medium bg-brand-500 hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-900 font-medium mb-1">Heads up</p>
        <p className="text-xs text-amber-800 leading-relaxed">
          T&amp;Cs are a legal document — keep them clear and accurate. The platform doesn't review
          studios' T&amp;Cs. If you change them, students enrolling after the change accept the new
          version. Consider noting the date at the top of your T&amp;Cs so students know which version
          they agreed to.
        </p>
      </div>
    </div>
  );
};

export default TCsTab;
